import Axios from 'axios';
import reduxStore from '../redux/store';
import { setSnackbar } from '../components/flashMessages/snackbar';
import i18n from '../i18n';
import interceptor from './interceptors/intercepter';
import errorHandler from './interceptors/errorHandler';
import { getSecureOAuthToken } from './tokenManager';
import { sanitizeStoreNameForPath } from './securityValidation';

// const EventEmitter = require('events');

const { dispatch } = reduxStore;

// Simple EventEmitter implementation for browser compatibility
class AxiosEmitter {
  constructor() {
    this.events = {};
    this.maxListeners = 10;
  }

  on(event, listener) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  emit(event, ...args) {
    if (this.events[event]) {
      this.events[event].forEach(listener => listener(...args));
    }
  }

  setMaxListeners(max) {
    this.maxListeners = max;
  }
}

const axiosEmitter = new AxiosEmitter();
axiosEmitter.setMaxListeners(0);

const { VITE_S3_ENABLE } = import.meta.env;
const { VITE_API_URL } = import.meta.env;
const baseURL = {};
if (VITE_S3_ENABLE === 'true') {
  baseURL.baseURL = VITE_API_URL;
} else {
  baseURL.baseURL = '/';
}

const axios = Axios.create({
  baseURL: baseURL.baseURL,
  headers: {
    'Content-Type': 'application/json',
    Authorization: getSecureOAuthToken(),
    'X-Forwarded-Base': `${VITE_API_URL}cortex`,
  },
  timeout: 90000,
});

const emitRequestQueue = (newRequestQueueCount) => {
  axiosEmitter.emit('requestQueueUpdate', newRequestQueueCount);
};

const emitRequest = () => {
  axiosEmitter.emit('request');
};

const emitResponse = () => {
  axiosEmitter.emit('response');
};

const getSafeStoreNamePathSegment = () => {
  try {
    return sanitizeStoreNameForPath(localStorage.getItem('StoreName'));
  } catch (_) {
    return '';
  }
};

const getKeywordSearchRequestPath = () => (
  `cortex/searches/${getSafeStoreNamePathSegment()}/keywords/form?followlocation&zoom=element:definition,element:code`
);

let reqQueueCount = 0;
const assignRequestQueueInterceptor = (a) => {
  a.interceptors.request.use(
    (reqConfig) => {
      if (!reqConfig?.noLoader && reqConfig?.url !== getKeywordSearchRequestPath()) {
        emitRequestQueue(++reqQueueCount);
      }
      emitRequest();
      return reqConfig;
    },
    (error) => {
      emitRequestQueue(Math.max(0, --reqQueueCount));
      dispatch(setSnackbar(true, 'error', i18n.t('common.labels.commonError')));
      return Promise.reject(error);
    },
  );

  a.interceptors.response.use(
    (response) => {
      if (!response.config?.noLoader && response.config?.url !== getKeywordSearchRequestPath()) {
        emitRequestQueue(Math.max(0, --reqQueueCount));
      }
      emitResponse();
      return response;
    },
    (error) => {
      const config = error?.config || error?.response?.config;
      if (!config?.noLoader && config?.url !== getKeywordSearchRequestPath()) {
        emitRequestQueue(Math.max(0, --reqQueueCount));
      }
      emitResponse();
      if (config?.skipGlobalErrorHandler) {
        return Promise.reject(error);
      }
      return Promise.resolve(error.response);
    },
  );
  return a;
};

export const generateAxiosInstance = (config) => {
  const a = Axios.create(config);
  assignRequestQueueInterceptor(a);
  return a;
};

assignRequestQueueInterceptor(axios);

axios.interceptors.response.use(interceptor, error => errorHandler(error));

export default axios;
export { axiosEmitter };